// Calcul des manœuvres d'un tracé avec la cartographie (« map matching ») :
// le tracé est recalé sur les vraies routes par Valhalla (serveur public OpenStreetMap, sans clé),
// ce qui donne les intersections, les ronds-points (numéro de sortie) et les noms de rue.
// Là où le service ne répond pas ou s'écarte du tracé, on se rabat sur la forme du tracé (detectTurns).

import { cumulativeDistances, distanceM, LatLng, pointAtAlong, simplify, snapToRoute } from './geo';
import { detectTurns, Maneuver, maneuversFromSteps, withArrival } from './maneuvers';
import { t } from './i18n';
import type { OrsRoute } from './ors';
import { fetchValhallaTrace } from './valhalla';

const SPACING_M = 30; // un point tous les 30 m envoyé au service
const CHUNK = 500; // points par requête (~15 km)
const PAUSE_MS = 1100; // serveur public : environ 1 requête / seconde
const MAX_FAILURES = 2; // morceaux ratés d'affilée avant d'abandonner le service (calcul trop long sinon)

export type ManeuverSource = 'carto' | 'mixte' | 'forme';

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

/** Découpe le tracé en morceaux de points régulièrement espacés : [{from, to, points}] */
export function matchChunks(route: LatLng[], cum = cumulativeDistances(route), spacing = SPACING_M, size = CHUNK) {
  const total = cum[cum.length - 1] ?? 0;
  const alongs: number[] = [];
  for (let s = 0; s < total; s += spacing) alongs.push(s);
  alongs.push(total);
  const chunks: { from: number; to: number; points: LatLng[] }[] = [];
  for (let i = 0; i < alongs.length - 1; i += size - 1) {
    const part = alongs.slice(i, i + size);
    if (part.length < 2) break;
    chunks.push({
      from: part[0],
      to: part[part.length - 1],
      points: part.map((a) => pointAtAlong(route, cum, a)),
    });
  }
  return chunks;
}

export type Interval = [number, number];

/**
 * Compare l'itinéraire recalé par le service au tracé imposé :
 * - `covered` : portions du tracé (m depuis le départ) où l'itinéraire le suit (à moins de 75 m) ;
 * - `maneuvers` : uniquement les étapes situées sur le tracé ET suivies de 120 m qui restent sur le tracé
 *   (sinon c'est un virage d'une autre route, à ne pas annoncer).
 */
export function matchServiceRoute(r: OrsRoute, route: LatLng[], cum: number[], from = 0, to = Infinity) {
  const NEAR_M = 75; // tolérance pour les GPX peu détaillés
  const AFTER_M = 120;
  const lo = Math.max(0, from - 300);
  const hi = to + 300;
  const alongs = r.points.map((p) => {
    const snap = snapToRoute(p, route, cum, lo, hi);
    return snap && snap.distance <= NEAR_M ? snap.along : null;
  });

  // portions couvertes : points consécutifs proches du tracé
  const covered: Interval[] = [];
  for (let i = 1; i < alongs.length; i++) {
    const a = alongs[i - 1];
    const b = alongs[i];
    if (a == null || b == null) continue;
    const seg: Interval = [Math.min(a, b), Math.max(a, b)];
    const last = covered[covered.length - 1];
    if (last && seg[0] <= last[1] + 5) last[1] = Math.max(last[1], seg[1]);
    else covered.push(seg);
  }

  // étapes fiables : le début et les 120 m suivants de l'itinéraire restent sur le tracé
  const trusted = r.steps.filter((_, k) => {
    let i = r.stepStarts[k] ?? 0;
    if (alongs[i] == null) return false;
    let run = 0;
    while (i + 1 < r.points.length && run < AFTER_M) {
      run += distanceM(r.points[i], r.points[i + 1]);
      i++;
      if (alongs[i] == null) return false;
    }
    return true;
  });
  const maneuvers = maneuversFromSteps(trusted, route, cum, from, to, NEAR_M).filter(
    (m) => m.along >= from && m.along <= to,
  );
  return { maneuvers, covered, points: r.points };
}

const insideAny = (x: number, intervals: Interval[], margin = 50) =>
  intervals.some(([a, b]) => x >= a - margin && x <= b + margin);

const coveredLength = (intervals: Interval[], from: number, to: number) =>
  intervals.reduce((sum, [a, b]) => sum + Math.max(0, Math.min(b, to) - Math.max(a, from)), 0);

/** Morceau recalé par Valhalla ; null si le service n'a pas répondu. */
async function serviceChunk(
  c: { from: number; to: number; points: LatLng[] },
  route: LatLng[],
  cum: number[],
  problems: string[],
) {
  // service parfois occupé : une deuxième tentative après une pause
  let lastError = '';
  for (let attempt = 0; attempt < 2; attempt++) {
    if (attempt > 0) await sleep(3000);
    try {
      const r = await fetchValhallaTrace(c.points);
      if (r) return matchServiceRoute(r, route, cum, c.from, c.to);
      lastError = t('réponse vide', 'empty response');
    } catch (e) {
      lastError = (e as Error).message;
    }
  }
  problems.push(lastError);
  return null;
}

/** L'itinéraire reste-t-il sur le tracé ? (95 % des points à moins de 40 m, aucun à plus de 150 m) */
export function followsRoute(path: LatLng[], route: LatLng[], cum: number[], from = 0, to = Infinity): boolean {
  if (path.length < 2) return false;
  let far = 0;
  for (const p of path) {
    const snap = snapToRoute(p, route, cum, Math.max(0, from - 300), to + 300);
    const d = snap ? snap.distance : Infinity;
    if (d > 150) return false;
    if (d > 40) far++;
  }
  return far / path.length <= 0.05;
}

export async function computeManeuvers(
  route: LatLng[],
  onProgress?: (done: number, total: number) => void,
): Promise<{
  maneuvers: Maneuver[];
  source: ManeuverSource;
  service: string;
  problems: string[];
  /** tracé dessiné sur les routes (null si le service n'a rien recalé) */
  line: DisplayLine | null;
}> {
  const cum = cumulativeDistances(route);
  const problems: string[] = [];
  const chunks = matchChunks(route, cum);
  const fallback = detectTurns(route, cum).filter((m) => m.kind !== 'arrive');
  const all: Maneuver[] = [];
  const total = cum[cum.length - 1] ?? 0;
  let mapped = 0; // longueur du tracé couverte par la cartographie
  let failures = 0; // morceaux ratés d'affilée
  const pieces: MatchedPiece[] = []; // géométrie recalée sur les routes, pour dessiner le tracé

  for (let i = 0; i < chunks.length; i++) {
    const c = chunks[i];
    onProgress?.(i, chunks.length);
    const inChunk = (m: Maneuver) => m.along >= c.from && m.along < c.to;

    if (failures < MAX_FAILURES) {
      if (i > 0) await sleep(PAUSE_MS);
      const res = await serviceChunk(c, route, cum, problems);
      failures = res ? 0 : failures + 1;
      if (failures === MAX_FAILURES) {
        problems.push(
          t(
            'service indisponible, la suite du tracé utilise sa forme',
            'service unavailable, the rest of the route uses its shape',
          ),
        );
      }
      if (res) {
        pieces.push({ from: c.from, points: res.points });
        all.push(...res.maneuvers);
        // là où le recalage s'écarte du tracé : forme du tracé
        all.push(...fallback.filter((m) => inChunk(m) && !insideAny(m.along, res.covered)));
        mapped += coveredLength(res.covered, c.from, c.to);
        if (coveredLength(res.covered, c.from, c.to) < (c.to - c.from) * 0.9) {
          problems.push(
            t("le recalage s'écarte du tracé par endroits", 'the matched route leaves the track in places'),
          );
        }
        continue;
      }
    }
    all.push(...fallback.filter(inChunk));
  }
  onProgress?.(chunks.length, chunks.length);

  all.sort((a, b) => a.along - b.along);
  const unique = all.filter((m, i) => i === 0 || m.along - all[i - 1].along >= 15);
  const share = total > 0 ? mapped / total : 0;
  const source: ManeuverSource = share >= 0.9 ? 'carto' : share > 0.05 ? 'mixte' : 'forme';
  return {
    maneuvers: withArrival(unique, route, cum),
    source,
    service: 'Valhalla (OpenStreetMap)',
    // problèmes distincts, pour comprendre un calcul « forme du tracé »
    problems: [...new Set(problems)].slice(0, 3),
    line: buildDisplayLine(route, cum, pieces),
  };
}

// ---------- tracé dessiné sur les routes ----------

/** Morceau de géométrie recalée par le service (départ du morceau sur le tracé, en m). */
export type MatchedPiece = { from: number; points: LatLng[] };
/** Points [lat, lon, distance sur le tracé imposé (m)] : se découpe comme le tracé (sliceRoute). */
export type DisplayLine = [number, number, number][];

const ON_LINE_M = 60; // un point recalé plus loin du tracé est une autre route : ignoré

/**
 * Ligne à dessiner : la géométrie recalée sur les routes (sans les zigzags du GPS ou d'un GPX peu
 * précis) là où elle suit le tracé, et le tracé brut ailleurs (détour du service, morceau sans réponse).
 * Chaque point garde sa distance le long du tracé imposé : la progression, le « fait / à faire » et le
 * suivi restent ceux du tracé. Les points sont rattachés au tracé dans l'ordre, dans une fenêtre qui
 * avance (un aller-retour par la même route ne mélange pas l'aller et le retour).
 */
export function buildDisplayLine(route: LatLng[], cum: number[], pieces: MatchedPiece[]): DisplayLine | null {
  const total = cum[cum.length - 1] ?? 0;
  if (route.length < 2 || !pieces.length) return null;
  type P = { point: LatLng; along: number };
  const kept: P[] = [];
  let last = 0;
  for (const piece of pieces) {
    let prev: LatLng | null = null;
    let cursor = Math.max(last, piece.from) - 50;
    for (const p of piece.points) {
      const reach = prev ? distanceM(prev, p) * 2 + 200 : 400;
      prev = p;
      const snap = snapToRoute(p, route, cum, Math.max(0, cursor - 30), cursor + reach);
      if (!snap || snap.distance > ON_LINE_M) continue;
      const along = Math.max(snap.along, last);
      kept.push({ point: p, along });
      last = along;
      cursor = along;
    }
  }
  if (!kept.length) return null;

  const out: P[] = [];
  const raw = (from: number, to: number) => {
    for (let i = 0; i < route.length; i++)
      if (cum[i] > from && cum[i] < to) out.push({ point: route[i], along: cum[i] });
  };
  out.push({ point: route[0], along: 0 });
  raw(0, kept[0].along);
  kept.forEach((k, i) => {
    const before = kept[i - 1];
    // trou : le tracé fait un chemin que la ligne droite entre deux points recalés ne suit pas
    if (before && k.along - before.along > distanceM(before.point, k.point) * 1.5 + 50) raw(before.along, k.along);
    out.push(k);
  });
  raw(last, total);
  out.push({ point: route[route.length - 1], along: total });

  const simple = simplify(
    out.map((o) => ({ ...o.point, along: o.along })),
    2,
  );
  return simple.map((p) => [Number(p.latitude.toFixed(6)), Number(p.longitude.toFixed(6)), Math.round(p.along)]);
}

/** Ligne enregistrée -> points et distances le long du tracé (à passer à sliceRoute). */
export function displayLinePoints(line: DisplayLine): { points: LatLng[]; alongs: number[] } {
  return {
    points: line.map(([latitude, longitude]) => ({ latitude, longitude })),
    alongs: line.map((p) => p[2]),
  };
}

/** Origine des instructions, en clair */
export function sourceLabel(source: string | null | undefined): string {
  switch (source) {
    case 'carto':
      return t('cartographie (ronds-points et rues)', 'map data (roundabouts and streets)');
    case 'mixte':
      return t('cartographie partielle', 'partial map data');
    case 'forme':
      return t('forme du tracé (sans ronds-points)', 'track shape (no roundabouts)');
    default:
      return source ?? '';
  }
}
